import type { MenuItemProps } from "@mantine/core";
import { DateTime } from "luxon";
import type { DateInputOnChangeValue } from "./DateRangeInput";

const DateRangePresets = {
  Years: "Years",
  Months: "Months",
  YearsAndMonths: "YearsAndMonths"
} as const;

type PresetAsMenuItem = Omit<MenuItemProps, "children"> & {
  label: string;
  onClick: () => void;
};

function getPresetYears({
  startYear = DateTime.now().minus({ years: 5 }).year,
  endYear,
  onChange
}: {
  startYear?: number;
  endYear?: number;
  onChange: (value: DateInputOnChangeValue) => void;
}): Array<PresetAsMenuItem> {
  const end = endYear || DateTime.now().year;
  const years: Array<PresetAsMenuItem> = [];
  for (let iteratorYear = startYear; iteratorYear <= end; iteratorYear++) {
    years.push({
      label: iteratorYear.toString(),
      onClick: () =>
        onChange([
          DateTime.fromObject({ year: iteratorYear, month: 1, day: 1 }),
          DateTime.fromObject({ year: iteratorYear, month: 12, day: 31 })
        ])
    });
  }
  return years.reverse();
}

function getPresetMonths({
  startMonth = DateTime.now().minus({ months: 12 }),
  endMonth,
  onChange
}: {
  startMonth?: DateTime;
  endMonth?: DateTime;
  onChange: (value: DateInputOnChangeValue) => void;
}): Array<PresetAsMenuItem> {
  const end = endMonth?.startOf("month") || DateTime.now().startOf("month");
  const months: Array<PresetAsMenuItem> = [];
  for (
    let iteratorMonthInYear = startMonth.startOf("month");
    iteratorMonthInYear <= end;
    iteratorMonthInYear = iteratorMonthInYear.plus({ months: 1 })
  ) {
    months.push({
      label: iteratorMonthInYear.toFormat("LLLL yyyy"),
      onClick: () =>
        onChange([
          iteratorMonthInYear.startOf("month"),
          iteratorMonthInYear.endOf("month")
        ])
    });
  }
  return months.reverse();
}

function getPresetYearsAndMonths({
  startMonth = DateTime.now().minus({ months: 12 }),
  endMonth,
  onChange
}: {
  startMonth?: DateTime;
  endMonth?: DateTime;
  onChange: (value: DateInputOnChangeValue) => void;
}): Array<PresetAsMenuItem> {
  const end = endMonth?.startOf("month") || DateTime.now().startOf("month");
  const yearsAndMonths: Array<PresetAsMenuItem> = [];
  for (
    let iteratorMonthInYear = startMonth.startOf("month");
    iteratorMonthInYear <= end;
    iteratorMonthInYear = iteratorMonthInYear.plus({ months: 1 })
  ) {
    yearsAndMonths.push({
      label: iteratorMonthInYear.toFormat("LLLL yyyy"),
      onClick: () =>
        onChange([
          iteratorMonthInYear.startOf("month"),
          iteratorMonthInYear.endOf("month")
        ])
    });
    if (iteratorMonthInYear.month === 12) {
      yearsAndMonths.push({
        label: iteratorMonthInYear.toFormat("yyyy"),
        onClick: () =>
          onChange([
            iteratorMonthInYear.startOf("year"),
            iteratorMonthInYear.endOf("year")
          ])
      });
    }
  }
  return yearsAndMonths.reverse();
}

export {
  DateRangePresets,
  getPresetMonths,
  getPresetYears,
  getPresetYearsAndMonths
};
